import { useState, useEffect } from "react"
import { useSelector } from "react-redux"
import useAds from "@hooks/useAds"
import { extractDriveId } from "@components/SortableItem/helper"
import { Button } from "@components/Button"
import { Autocomplete, TextField } from "@mui/material"
import { useNavigate } from "react-router-dom"
import Navbar from "@components/Navbar/Navbar"
import { ExternalLink, Copy } from "lucide-react"
import useLandingPages from "@hooks/useLandingPages"
import { notify } from "@utils/notify"
import useDefaultValues from "@hooks/useDefaultValues.js"
import { getResolvedDeeplinks } from "@services/getResolvedDeeplinks"
import { createFilterOptions } from "@mui/material/Autocomplete"

//const userId = import.meta.env.VITE_USER_ID

const MapLandingPage = () => {
  const userId = localStorage.getItem("userId")
  const storedAdAccountId = localStorage.getItem("adAccountId")
  const storedAdAccountName = localStorage.getItem("adAccountName")
  const filter = createFilterOptions()

  const [editingId, setEditingId] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [inputValue, setInputValue] = useState("")
  const [selectedUrlObject, setSelectedUrlObject] = useState(null)
  const [originalUrl, setOriginalUrl] = useState("")
  const [previewImage, setPreviewImage] = useState(null)
  const [adNames, setAdNames] = useState({})
  const [editingNameId, setEditingNameId] = useState(null)
  const [originalName, setOriginalName] = useState("")
  const [copiedAdNames, setCopiedAdNames] = useState([])
  const [resolvedDeeplinks, setResolvedDeeplinks] = useState([])
  const [resolvingDeeplinks, setResolvingDeeplinks] = useState(false)
  const [savingStates, setSavingStates] = useState({}) // { adId: true/false }
  const [savingURLStates, setSavingURLStates] = useState({}) // { adId: true/false }

  const { ads } = useSelector((state) => state.ads)
  const { defaultValues } = useSelector((state) => state.defaultValues)

  const { handleGetAdsWithCreatives, handleUpdateAd } = useAds()
  const { handleGetAllLandingPages } = useLandingPages()
  const { handleGetDefaultValues } = useDefaultValues()
  const navigate = useNavigate()

  const handleLandingPageChange = (adId, value) => {
    const newValue = value || ""
    setInputValue(newValue.trim())
  }

  const handleSaveAndNext = () => {
    const adsWithoutUrls = ads?.filter(
      (ad) => ad.creatives.length > 0 && !ad.landingPageUrl?.trim()
    )
    if (adsWithoutUrls?.length > 0) {
      notify(
        "Please add landing page URLs for all ads before proceeding",
        "error"
      )
      return
    }
    navigate("/ad-copy")
  }

  const handleSaveUrl = async (adId) => {
    setIsSaving(true)
    setSavingURLStates((prev) => ({ ...prev, [adId]: true }))
    try {
      let urlToSave
      if (selectedUrlObject) {
        urlToSave = selectedUrlObject.originalUrl
      } else {
        urlToSave = inputValue
          .trim()
          .replace(/\s+/g, "")
          .replace(/[-\s]+$/, "")
      }

      if (!urlToSave) {
        notify("Landing page URL cannot be empty", "error")
        return
      }

      await handleUpdateAd(adId, { landingPageUrl: urlToSave })

      await handleGetAdsWithCreatives(userId, defaultValues?.creativeLocationId)

      setEditingId(null)
      setInputValue("")
      setSelectedUrlObject(null)
      setOriginalUrl("")
      notify("Landing page URL updated successfully", "success")
    } catch (error) {
      console.error("Failed to save landing page URL:", error)
      notify("Failed to save landing page URL", "error")
    } finally {
      setIsSaving(false)
      setSavingURLStates((prev) => ({ ...prev, [adId]: false }))
    }
  }

  const handleSaveName = async (adId) => {
    setSavingStates((prev) => ({ ...prev, [adId]: true }))
    try {
      await handleUpdateAd(adId, { name: adNames[adId] })
      setEditingNameId(null)
      notify("Ad name updated successfully", "success")
    } catch (error) {
      console.log("error:", error)
      notify("Failed to update ad name", "error")
      setAdNames((prev) => ({
        ...prev,
        [adId]: originalName,
      }))
    } finally {
      setSavingStates((prev) => ({ ...prev, [adId]: false }))
    }
  }

  const handleNameChange = (adId, value) => {
    setAdNames((prev) => ({
      ...prev,
      [adId]: value,
    }))
  }

  const handleCopyAdNames = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText()
      if (clipboardText) {
        const adNamesArray = clipboardText
          .split("\n")
          .map((name) => name.trim())
          .filter((name) => name.length > 0)

        setCopiedAdNames(adNamesArray)
        notify(
          `Successfully loaded ${adNamesArray.length} ad names from clipboard`,
          "success"
        )
      } else {
        notify("No text found in clipboard", "error")
      }
    } catch (error) {
      console.error("Failed to read clipboard:", error)
      notify(
        "Failed to read clipboard. Please ensure clipboard access is allowed.",
        "error"
      )
    }
  }

  const handleCopyUrls = async () => {
    try {
      setResolvingDeeplinks(true)
      const clipboardText = await navigator.clipboard.readText()
      if (clipboardText) {
        const deeplinksArray = clipboardText
          .split("\n")
          .map((name) => name.trim())
          .filter((name) => name.length > 0)

        const response = await getResolvedDeeplinks(deeplinksArray)
        const resolvedDeeplinks = response?.data?.data

        const transformedUrls =
          resolvedDeeplinks?.map((item, index) => ({
            id: `url-${index}`,
            originalUrl: item.original_url,
            resolvedUrl: item.resolved_url,
          })) || []

        setResolvedDeeplinks(transformedUrls)
        notify(
          `Successfully loaded ${transformedUrls.length} URLs from clipboard`,
          "success"
        )
      } else {
        notify("No text found in clipboard", "error")
      }
    } catch (error) {
      console.error("Failed to read clipboard:", error)
      notify(
        "Failed to read clipboard. Please ensure clipboard access is allowed.",
        "error"
      )
    } finally {
      setResolvingDeeplinks(false)
    }
  }

  const handleApplyUrlToAll = async (urlToApply) => {
    if (!urlToApply || !urlToApply.trim()) {
      notify("Please enter a landing page URL to apply", "error")
      return
    }

    const formattedUrl = urlToApply
      .trim()
      .replace(/\s+/g, "")
      .replace(/[-\s]+$/, "")

    setIsSaving(true)
    try {
      const updatePromises = ads?.map((ad) =>
        handleUpdateAd(ad._id, {
          landingPageUrl: formattedUrl,
          name: ad.name || adNames[ad._id],
        })
      )

      await Promise.all(updatePromises)
      await handleGetAdsWithCreatives(userId, defaultValues?.creativeLocationId)

      setEditingId(null)
      setInputValue("")
      setOriginalUrl("")
      notify("Landing page URL applied to all ads successfully", "success")
    } catch (error) {
      console.error("Failed to apply landing page URL to all ads:", error)
      notify("Failed to apply landing page URL to all ads", "error")
    } finally {
      setIsSaving(false)
    }
  }

  useEffect(() => {
    handleGetDefaultValues(userId, storedAdAccountId)
  }, [userId, storedAdAccountId])

  useEffect(() => {
    if (userId && defaultValues?.creativeLocationId)
      handleGetAdsWithCreatives(userId, defaultValues?.creativeLocationId)
  }, [userId, defaultValues])

  useEffect(() => {
    if (ads?.length) {
      const initialAdNames = ads.reduce((acc, ad) => {
        acc[ad._id] = ad.name || ""
        return acc
      }, {})
      setAdNames(initialAdNames)
    }
  }, [ads])

  useEffect(() => {
    handleGetAllLandingPages()
  }, [])

  return (
    <div className="max-w-5xl mx-auto flex flex-col p-6 bg-white min-h-screen">
      <Navbar
        title="Map Landing Pages"
        onSaveAndNext={handleSaveAndNext}
        isSaving={isSaving}
        adAccountName={storedAdAccountName}
      />

      <div className="flex items-end justify-end gap-2">
        <Button
          text="Load Ad Names"
          size="md"
          variant="transparent"
          className="font-medium rounded-lg transition-colors text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100"
          onClick={handleCopyAdNames}
        />
        <Button
          text="Load URLs"
          size="md"
          variant="transparent"
          loading={resolvingDeeplinks}
          disabled={resolvingDeeplinks}
          className="font-medium rounded-lg transition-colors text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100"
          onClick={handleCopyUrls}
        />
      </div>

      <div className="space-y-4" key={ads?.length}>
        {ads?.map((ad) => {
          return ad?.creatives?.length > 0 ? (
            <div key={ad._id} className="max-w-4xl mx-auto space-y-3">
              <div className="flex items-center gap-2 py-2">
                <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                  {ad.creatives.length} creatives
                </span>
              </div>
              <div className="flex items-center gap-6 p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="flex items-center gap-6 flex-col relative">
                  {ad.creatives.map((creative) => {
                    return (
                      <div
                        key={creative._id}
                        className="flex items-center gap-5 relative"
                        onMouseEnter={() => setPreviewImage(creative)}
                        onMouseLeave={() => setPreviewImage(null)}
                      >
                        <img
                          src={
                            creative.driveThumbnail ||
                            `https://drive.google.com/thumbnail?sz=w256-h100&id=${extractDriveId(
                              creative.fileLink
                            )}`
                          }
                          alt={creative.fileName}
                          className="max-w-32 max-h-32 object-cover rounded"
                        />
                        <div className="min-w-[200px]">
                          <div className="font-medium max-w-[180px] text-sm">
                            <span className=" break-words">
                              {creative.fileName}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {creative.resolution}
                          </div>
                        </div>

                        {previewImage?._id === creative._id && (
                          <div className="absolute left-full ml-4 top-1/2 -translate-y-1/2 z-50">
                            <div className="bg-white rounded-lg shadow-xl border border-gray-200 p-2">
                              {creative.fileType === "video/mp4" ? (
                                <video
                                  src={creative?.previewLink}
                                  className="max-w-[500px] max-h-[300px] object-contain rounded"
                                  autoPlay
                                  muted
                                  loop
                                />
                              ) : (
                                <img
                                  src={creative?.previewLink}
                                  alt={creative.fileName}
                                  className="max-w-[500px] max-h-[300px] object-contain rounded"
                                />
                              )}
                              <div className="mt-2 text-xs text-gray-500 text-center">
                                {creative.resolution}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
                <div className="flex-1 space-y-4">
                  {/* Ad Name Section */}
                  <div className="flex items-center gap-4">
                    {editingNameId === ad._id ? (
                      <>
                        <Autocomplete
                          freeSolo
                          className="flex-1"
                          value={adNames[ad._id] ?? ad.name ?? ""}
                          onChange={(_, newValue) => {
                            handleNameChange(ad._id, newValue || "")
                          }}
                          onInputChange={(_, newValue) => {
                            handleNameChange(ad._id, newValue)
                          }}
                          options={copiedAdNames}
                          renderOption={(props, option) => {
                            const { key, ...otherProps } = props
                            return (
                              <li key={key} {...otherProps}>
                                <div className="text-sm px-2 py-1 break-all">
                                  {option}
                                </div>
                              </li>
                            )
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#E5E7EB",
                                  borderWidth: "1px",
                                },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#E5E7EB",
                              },
                            },
                            "& .MuiAutocomplete-paper": {
                              maxHeight: "200px",
                              "& .MuiAutocomplete-listbox": {
                                padding: "4px 0",
                              },
                            },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Enter ad name"
                              size="small"
                              autoFocus
                            />
                          )}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault()
                              handleSaveName(ad._id)
                            }
                          }}
                        />
                        <Button
                          text="Cancel"
                          size="md"
                          variant="transparent"
                          className="px-5 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 rounded transition-colors"
                          onClick={() => {
                            setEditingNameId(null)
                            handleNameChange(ad._id, originalName)
                          }}
                        />
                        <Button
                          text={savingStates[ad._id] ? "Saving..." : "Save"}
                          loading={savingStates[ad._id]}
                          disabled={savingStates[ad._id]}
                          size="md"
                          variant="transparent"
                          className="px-5 py-1.5 text-sm font-medium text-emerald-600 hover:text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded transition-colors"
                          onClick={() => handleSaveName(ad._id)}
                        />
                      </>
                    ) : (
                      <>
                        <div className="flex-1 flex flex-col gap-2 max-w-[20rem]">
                          <span className="text-sm font-medium text-gray-400">
                            Ad Name
                          </span>
                          <span className="text-[16px] break-words">
                            {adNames[ad._id] || ad.name || "Untitled Ad"}
                          </span>
                        </div>
                        <Button
                          text="Edit"
                          size="md"
                          variant="transparent"
                          className="px-5 ml-auto py-1.5 text-sm font-medium text-gray-600 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                          onClick={() => {
                            setOriginalName(adNames[ad._id] || "")
                            setEditingNameId(ad._id)
                          }}
                        />
                      </>
                    )}
                  </div>

                  {/* URL Section */}
                  <div className="flex items-center gap-4">
                    {editingId === ad._id ? (
                      <>
                        <Autocomplete
                          freeSolo
                          className="flex-1"
                          inputValue={inputValue}
                          onInputChange={(_, newValue) => {
                            setInputValue(newValue)
                            if (
                              selectedUrlObject &&
                              newValue !== selectedUrlObject?.originalUrl
                            ) {
                              setSelectedUrlObject(null)
                            }
                          }}
                          onChange={(_, newValue) => {
                            if (
                              typeof newValue === "object" &&
                              newValue !== null
                            ) {
                              setSelectedUrlObject(newValue)
                              setInputValue(newValue.originalUrl)
                            } else {
                              setSelectedUrlObject(null)
                              setInputValue(newValue || "")
                            }
                          }}
                          options={resolvedDeeplinks}
                          getOptionLabel={(option) =>
                            typeof option === "string"
                              ? option
                              : option.originalUrl
                          }
                          filterOptions={(options, params) => {
                            const filtered = filter(options, {
                              ...params,
                              getOptionLabel: (opt) => opt.resolvedUrl,
                            })
                            return filtered
                          }}
                          renderOption={(props, option) => {
                            const { key, ...otherProps } = props
                            return (
                              <li key={option.id || key} {...otherProps}>
                                <div className="px-2 py-1 break-all flex flex-col">
                                  <span className="font-medium text-sm">
                                    {option.resolvedUrl}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    Short link: {option.originalUrl}
                                  </span>
                                </div>
                              </li>
                            )
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Enter landing page URL"
                              size="small"
                              autoFocus
                            />
                          )}
                        />
                        <Button
                          text="Cancel"
                          size="md"
                          variant="transparent"
                          className="px-5 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 rounded transition-colors"
                          onClick={() => {
                            setEditingId(null)
                            setInputValue("")
                            setSelectedUrlObject(null)
                            handleLandingPageChange(ad._id, originalUrl)
                          }}
                        />
                        <Button
                          text={savingURLStates[ad._id] ? "Saving..." : "Save"}
                          loading={savingURLStates[ad._id]}
                          disabled={savingURLStates[ad._id]}
                          size="md"
                          variant="transparent"
                          className="px-5 py-1.5 text-sm font-medium text-emerald-600 hover:text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded transition-colors"
                          onClick={() => handleSaveUrl(ad._id)}
                        />
                      </>
                    ) : (
                      <>
                        <div className="flex-1 flex flex-col gap-2">
                          <span className="text-sm font-medium text-gray-400">
                            Landing Page URL
                          </span>
                          {ad?.landingPageUrl ? (
                            <a
                              href={
                                ad?.landingPageUrl?.startsWith("http")
                                  ? ad?.landingPageUrl
                                  : `https://${ad?.landingPageUrl}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center text-[16px] gap-2 underline group"
                            >
                              <div className="max-w-[350px] break-all">
                                <span className="flex flex-col gap-2">
                                  {ad?.landingPageUrl}
                                </span>
                              </div>
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          ) : (
                            <span className="text-gray-500">
                              No landing page URL set
                            </span>
                          )}
                        </div>
                        {ads?.indexOf(ad) === 0 && ad?.landingPageUrl && (
                          <button
                            onClick={() =>
                              handleApplyUrlToAll(ad?.landingPageUrl)
                            }
                            className="flex items-center justify-center gap-1 text-xs bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors duration-200 py-1 px-2 rounded-md font-medium border border-blue-200"
                          >
                            <Copy size={11} />
                            Apply to all
                          </button>
                        )}
                        <Button
                          text="Edit"
                          size="md"
                          variant="transparent"
                          className="px-5 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                          onClick={() => {
                            setOriginalUrl(ad?.landingPageUrl || "")
                            setEditingId(ad._id)
                            setInputValue(ad?.landingPageUrl || "")
                          }}
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : null
        })}
      </div>
    </div>
  )
}

export default MapLandingPage